'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createLocation, askQwen } from '@/lib/actions';
import { ArrowLeft, Sparkles, Save, Wand2 } from 'lucide-react';
import Link from 'next/link';

export default function NewLocationPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [brainDump, setBrainDump] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    lore: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBrainDump = async () => {
    if (!brainDump.trim()) return;
    setIsParsing(true);
    try {
      const prompt = `Parse this messy brain dump into a JSON object for a story location. The brain dump: "${brainDump}". Return ONLY JSON with: name, type, lore.`;
      const response = await askQwen(prompt, "You extract location details into JSON.");
      const jsonStr = response.match(/\{[\s\S]*\}/)?.[0];
      if (jsonStr) {
        setFormData(JSON.parse(jsonStr));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsParsing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    data.append('custom_fields', '[]');
    try {
      const id = await createLocation(data);
      router.push(`/locations/${id}`);
      router.refresh();
    } catch (err) {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col p-8">
      <Link href="/locations" className="flex items-center gap-2 text-slate-400 mb-8"><ArrowLeft size={20} /> Back to Atlas</Link>
      <div className="max-w-4xl mx-auto w-full grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
           <h2 className="text-3xl font-serif font-bold text-blue-400 italic flex items-center gap-2"><Sparkles /> The Oracle's Vision</h2>
           <textarea value={brainDump} onChange={(e) => setBrainDump(e.target.value)} placeholder="Describe the place messy... 'A high tower in the clouds where the birds talk...'" className="w-full h-48 bg-slate-900 border border-slate-800 rounded-2xl p-4 text-sm" />
           <button onClick={handleBrainDump} disabled={isParsing} className="w-full bg-blue-600 py-3 rounded-xl font-bold flex items-center justify-center gap-2">
             {isParsing ? 'Oracle is mapping...' : <><Wand2 size={18} /> Manifest Location</>}
           </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 bg-slate-900/50 p-8 rounded-3xl border border-slate-800">
           <input required name="name" value={formData.name} onChange={handleChange} placeholder="Location Name" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
           <input name="type" value={formData.type} onChange={handleChange} placeholder="Type (e.g. Kingdom, Ruin, Forest)" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
           <textarea name="lore" value={formData.lore} onChange={handleChange} placeholder="Lore & History" rows={8} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4" />
           <button type="submit" disabled={loading} className="w-full bg-emerald-600 py-4 rounded-xl font-bold flex items-center justify-center gap-2"><Save /> {loading ? 'Saving...' : 'Create Location'}</button>
        </form>
      </div>
    </div>
  );
}
